const SITECORE_REQUIRED_ATTRIBUTE = 'data-val-required';
const SITECORE_ISCHECKED_ATTRIBUTE = 'data-val-ischecked';
const SITECORE_REGEX_VALUE_ATTRIBUTE = 'data-val-regex';
const SITECORE_REGEX_PATTERN_ATTRIBUTE = 'data-val-regex-pattern';
const SITECORE_FIELDNAME_ATTRIBUTE = 'data-sc-field-name';
const INVALID_CLASS = '-invalid';

export function parseSitecoreForm(formEl) {
  const formProperties = {
    formAction: null,
    formMethod: null,
  };
  formProperties.formAction = formEl?.getAttribute('action');
  formProperties.formMethod = formEl?.getAttribute('method');
  return formProperties;
}

export function bindFilledInputToggle(elm) {
  elm.addEventListener('change', () => {
    if (elm?.value?.length > 0) {
      elm.classList.add('-filled');
    } else {
      elm.classList.remove('-filled');
    }
  });
}

export function applyValidationError(inputEl, msgEl, err) {
  inputEl.classList.add(INVALID_CLASS);
  inputEl.setAttribute('aria-invalid', true);
  msgEl.id = `${inputEl.id}_Error`;
  inputEl.setAttribute('aria-describedby', msgEl.id);
  msgEl.innerText = err;
}

export function clearValidationError(inputEl, msgEl) {
  inputEl.classList.remove(INVALID_CLASS);
  inputEl.setAttribute('aria-invalid', false);
  inputEl.removeAttribute('aria-describedby');
  msgEl.innerText = '';
}

export function validateRequired(fieldInfo) {
  if (!fieldInfo.inputElement.value.length) {
    const validationError = fieldInfo.inputElement.getAttribute(
      SITECORE_REQUIRED_ATTRIBUTE
    );
    applyValidationError(
      fieldInfo.inputElement,
      fieldInfo.validationMsgTarget,
      validationError
    );
    return validationError;
  } else {
    clearValidationError(fieldInfo.inputElement, fieldInfo.validationMsgTarget);
  }
  return null;
}

export function validateChecked(fieldInfo) {
  if (!fieldInfo.inputElement.checked) {
    const validationError = fieldInfo.inputElement.getAttribute(
      SITECORE_ISCHECKED_ATTRIBUTE
    );
    applyValidationError(
      fieldInfo.inputElement,
      fieldInfo.validationMsgTarget,
      validationError
    );
    return validationError;
  } else {
    clearValidationError(fieldInfo.inputElement, fieldInfo.validationMsgTarget);
  }
  return null;
}

export function validatePattern(fieldInfo) {
  const matcher = new RegExp(
    fieldInfo.inputElement.getAttribute(SITECORE_REGEX_PATTERN_ATTRIBUTE)
  );
  if (!matcher.test(fieldInfo.inputElement.value)) {
    const validationError = fieldInfo.inputElement.getAttribute(
      SITECORE_REGEX_VALUE_ATTRIBUTE
    );
    applyValidationError(
      fieldInfo.inputElement,
      fieldInfo.validationMsgTarget,
      validationError
    );
    return validationError;
  } else {
    clearValidationError(fieldInfo.inputElement, fieldInfo.validationMsgTarget);
  }
  return null;
}

export function bindValidator(fieldInfo) {
  return {
    ...fieldInfo,
    validate: () => {
      if (!fieldInfo.inputElement) return [];
      let validationErrors = [];

      if (fieldInfo.inputElement.hasAttribute(SITECORE_REQUIRED_ATTRIBUTE)) {
        const result = validateRequired(fieldInfo);
        if (result) validationErrors.push(result);
      }

      if (fieldInfo.inputElement.hasAttribute(SITECORE_ISCHECKED_ATTRIBUTE)) {
        const result = validateChecked(fieldInfo);
        if (result) validationErrors.push(result);
      }

      if (fieldInfo.inputElement.hasAttribute(SITECORE_REGEX_VALUE_ATTRIBUTE)) {
        const result = validatePattern(fieldInfo);
        if (result) validationErrors.push(result);
      }

      return validationErrors;
    },
  };
}

export function processLabelField(labelNode) {
  const fieldInfo = {
    isNewsletterSignup: false,
    isTermsAgree: false,
    hiddenFields: [],
  };
  fieldInfo.labelTarget = labelNode.elm;
  // Check labels for children inputs (checkboxes)
  if (labelNode.children.length > 1) {
    labelNode.children.forEach((labelSubNode) => {
      if (labelSubNode.tag === 'input') {
        if (labelSubNode.data.attrs.type === 'hidden') {
          fieldInfo.hiddenFields.push({
            name: labelSubNode.data.attrs.name,
            value: labelSubNode.data.attrs.value,
          });
        } else {
          fieldInfo.isNewsletterSignup =
            labelSubNode.data.attrs[SITECORE_FIELDNAME_ATTRIBUTE] ===
            'newsletter_signup';
          fieldInfo.isTermsAgree =
            labelSubNode.data.attrs[SITECORE_FIELDNAME_ATTRIBUTE] ===
            'agree_terms';
          fieldInfo.inputElement = labelSubNode.elm;
        }
      }
    });
  }
  return fieldInfo;
}

export function processInputField(inputNode) {
  let inputInfo = {
    hiddenFields: [],
  };
  // Collect any related hidden nodes
  if (inputNode.data.attrs.type === 'hidden') {
    inputInfo.hiddenFields.push({
      name: inputNode.data.attrs.name,
      value: inputNode.data.attrs.value,
    });
  } else if (inputNode.data.attrs.type === 'submit') {
    inputInfo.inputElement = inputNode.elm;
  } else {
    // Text and email inputs
    bindFilledInputToggle(inputNode.elm);
    inputInfo.inputElement = inputNode.elm;
  }
  return inputInfo;
}

export function bindFormField(node) {
  let fieldInfo = {
    hiddenFields: [],
    labelTarget: null,
    validationMsgTarget: null,
    inputElement: null,
    nestedFieldInfo: [],
    isNewsletterSignup: false,
    isTermsAgree: false,
  };

  const mergeFieldInfo = (destination, source) => {
    return {
      ...destination,
      ...source,
      nestedFieldInfo: [
        ...destination.nestedFieldInfo,
        ...(source.nestedFieldInfo || []),
      ],
      hiddenFields: [
        ...destination.hiddenFields,
        ...(source.hiddenFields || []),
      ],
    };
  };

  if (node.tag === 'input') {
    const fieldNodeInfo = processInputField(node);
    const mergedInfo = mergeFieldInfo(fieldInfo, fieldNodeInfo);
    return bindValidator(mergedInfo);
  }

  node.children
    .filter((fieldNode) => fieldNode.tag !== undefined)
    .forEach((fieldNode) => {
      let fieldNodeInfo = {
        nestedFieldInfo: [],
        hiddenFields: [],
      };
      if (fieldNode.tag === 'div') {
        fieldNodeInfo.nestedFieldInfo.push(bindFormField(fieldNode));
      } else if (fieldNode.tag === 'label') {
        fieldNodeInfo = processLabelField(fieldNode);
      } else if (fieldNode.tag === 'input') {
        fieldNodeInfo = processInputField(fieldNode);
      } else if (fieldNode.tag === 'select') {
        bindFilledInputToggle(fieldNode.elm);
        fieldNodeInfo.inputElement = fieldNode.elm;
      } else if (fieldNode.tag === 'textarea') {
        fieldNodeInfo.inputElement = fieldNode.elm;
      } else if (fieldNode.tag === 'span') {
        fieldNodeInfo.validationMsgTarget = fieldNode.elm;
      }

      fieldInfo = mergeFieldInfo(fieldInfo, fieldNodeInfo);
    });

  return bindValidator(fieldInfo);
}
